import React, { useContext, useState } from "react";
import { Grid, MenuItem, Select, FormControl, ListItemText, Button } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import "../FlightRoster.css";
import DateLocalizationProvider from "../../../../components/element/DateLocalizationProvider";
import { StorageContext } from "../../../../services/StorageProvider";
import { DATE_FORMAT } from "../../../../utils/constants";
import { useFormData } from "../../../../helpers/FormDataProvider";
import SearchAirlineDialog from "../../../../components/element/SearchAirlineDialog";
import SearchAirportDialog from "../../../../components/element/SearchAirportDialog";
import BadgesFilterSelect from "../../../../components/element/BadgesFilterSelect";

const FlightRosterFilter = ({
  selectedAirline,
  setSelectedAirline,
  selectedArrivalAirport,
  setSelectedArrivalAirport,
  selectedDepartureAirport,
  setSelectedDepartureAirport,
  selectedDepartureDate,
  setSelectedDepartureDate,
  selectedBadges,
  setSelectedBadges,
  onClickClear,
  onClickApply,
  showDateFilter = true,
}) => {
  const { userData } = useFormData();
  const { airportData, associatedAirlineData } = useContext(StorageContext);
  const [showSearchAirlineDialog, setShowSearchAirlineDialog] = useState(false);
  const [showSearchArrivalAirportDialog, setShowSearchArrivalAirportDialog] = useState(false);
  const [showSearchDepartureAirportDialog, setShowSearchDepartureAirportDialog] = useState(false);

  const handleSearchAirlineChange = (airline) => {
    setSelectedAirline(airline);
  };

  const handleSearchArrivalAirportChange = (airport) => {
    setSelectedArrivalAirport(airport);
  };

  const handleSearchDepartureAirportChange = (airport) => {
    setSelectedDepartureAirport(airport);
  };

  const handleAirlineChange = (event) => {
    const selectedId = event.target.value;
    const selectedAirline = associatedAirlineData?.data?.find(
      (airline) => airline.id === selectedId
    );
    setSelectedAirline(selectedAirline);
  };

  const handleDepartureDateChange = (newValue) => {
    setSelectedDepartureDate(newValue);
  };

  const handleClearFilter = () => {
    setSelectedAirline(null);
    setSelectedArrivalAirport(null);
    setSelectedDepartureAirport(null);
    setSelectedDepartureDate(null);
    setSelectedBadges([]);
    onClickClear();
  };

  const isApplyButtonDisabled = () => {
    if (showDateFilter) {
      return !(
        selectedAirline ||
        selectedArrivalAirport ||
        selectedDepartureAirport ||
        selectedDepartureDate ||
        (userData?.isAAUser && selectedBadges.length > 0)
      );
    } else {
      return !(
        selectedAirline ||
        selectedArrivalAirport ||
        selectedDepartureAirport ||
        (userData?.isAAUser && selectedBadges.length > 0)
      );
    }
  };

  return (
    <div className="flight-roster-filter-container">
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl variant="outlined">
                <div className="flight-roster-filter-header">Airline</div>
                {userData?.isAAUser ? (
                  <div
                    className="flight-roster-filter-select-airline"
                    onClick={() => setShowSearchAirlineDialog(true)}
                  >
                    {selectedAirline ? (
                      <p>{selectedAirline?.airlineName}</p>
                    ) : (
                      <span className="placeholder-text">Select</span>
                    )}
                  </div>
                ) : (
                  <Select
                    id="flight-roster-filter-select-airline"
                    className="flight-roster-filter-select-container"
                    value={selectedAirline?.id || ""}
                    onChange={handleAirlineChange}
                    displayEmpty
                    renderValue={(selected) =>
                      selected ? (
                        associatedAirlineData?.data?.find(
                          (airline) => airline?.id === selected
                        )?.airlineName
                      ) : (
                        <span className="placeholder-text">Select</span>
                      )
                    }
                  >
                    {associatedAirlineData?.data?.map((airline) => (
                      <MenuItem key={airline?.id} value={airline?.id}>
                        <ListItemText primary={airline?.airlineName} />
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl variant="outlined">
                <div className="flight-roster-filter-header">
                  Arrival Airport
                </div>
                <div
                  className="flight-roster-filter-select-airline"
                  onClick={() => setShowSearchArrivalAirportDialog(true)}
                >
                  {selectedArrivalAirport ? (
                    <p>{selectedArrivalAirport?.name}</p>
                  ) : (
                    <span className="placeholder-text">Search</span>
                  )}
                </div>
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl variant="outlined">
                <div className="flight-roster-filter-header">
                  Departure Airport
                </div>
                <div
                  className="flight-roster-filter-select-airline"
                  onClick={() => setShowSearchDepartureAirportDialog(true)}
                >
                  {selectedDepartureAirport ? (
                    <p>{selectedDepartureAirport?.name}</p>
                  ) : (
                    <span className="placeholder-text">Search</span>
                  )}
                </div>
              </FormControl>
            </Grid>

            {userData?.isAAUser && (
              <Grid item>
                <FormControl variant="outlined">
                  <div className="flight-roster-filter-header">Badges</div>
                  <BadgesFilterSelect
                    selectedBadges={selectedBadges}
                    onChange={setSelectedBadges}
                    selectClassName="flight-roster-filter-select-container flight-roster-filter-select-badges"
                  />
                </FormControl>
              </Grid>
            )}

            {showDateFilter && (
              <DateLocalizationProvider>
                <Grid item>
                  <FormControl variant="outlined">
                    <div className="flight-roster-filter-header">
                      Departure Date
                    </div>
                    <MobileDatePicker
                      id="flight-roster-filter-departure-datepicker"
                      className="flight-roster-filter-datepicker"
                      format={DATE_FORMAT}
                      value={selectedDepartureDate}
                      onChange={handleDepartureDateChange}
                    />
                  </FormControl>
                </Grid>
              </DateLocalizationProvider>
            )}
          </Grid>
        </Grid>

        <Grid item>
          <div className="flight-roster-filter-button-container">
            <Button onClick={handleClearFilter}>Clear</Button>
            <Button
              variant="contained"
              color="secondaryButton"
              disabled={isApplyButtonDisabled()}
              onClick={onClickApply}
            >
              Apply
            </Button>
          </div>
        </Grid>
      </Grid>
      <SearchAirlineDialog
        open={showSearchAirlineDialog}
        onClose={() => setShowSearchAirlineDialog(false)}
        onSelectAirline={handleSearchAirlineChange}
      />
      <SearchAirportDialog
        open={showSearchArrivalAirportDialog}
        onClose={() => setShowSearchArrivalAirportDialog(false)}
        airportData={airportData}
        onSelectAirport={handleSearchArrivalAirportChange}
      />
      <SearchAirportDialog
        open={showSearchDepartureAirportDialog}
        onClose={() => setShowSearchDepartureAirportDialog(false)}
        airportData={airportData}
        onSelectAirport={handleSearchDepartureAirportChange}
      />
    </div>
  );
};

export default FlightRosterFilter;
